import { Auth0Provider, type Auth0ProviderOptions } from '@auth0/auth0-react';
import { QueryClientProvider as BaseQueryClientProvider, isCancelledError, QueryClient } from '@tanstack/react-query';
import { Provider as JotaiProvider } from 'jotai';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { DevAuth0Provider } from '~/contexts/dev-auth-provider';
import { handleApiError } from '~/errors/handleApiError';
import { useToast } from '~/global-state/toast';
import { useFormatMessage } from '~/intl';
import { IntlProvider } from '~/intl/index';

const auth0ProviderOptions: Auth0ProviderOptions = {
  authorizationParams: {
    audience: AUTH0_AUDIENCE,
    redirect_uri: `${window.location.origin}/callback`,
    scope: 'openid profile email offline_access',
  },
  cacheLocation: 'localstorage' as const,
  clientId: AUTH0_CLIENT_ID,
  domain: AUTH0_DOMAIN,
  useRefreshTokens: true,
};

export const QueryClientProvider = ({ children }: PropsWithChildren) => {
  const { addToast } = useToast();
  const formatMessage = useFormatMessage();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
          },
        },
      }),
  );

  // Handled through a cache subscription rather than `QueryCache.onError`, as the client is created once but the
  // handler must follow locale changes, including the switch from the default locale used during hydration
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'error' || isCancelledError(event.action.error)) {
          return;
        }

        const { disableGlobalErrorHandler, customErrorHandler } = event.query.meta || {};

        if (disableGlobalErrorHandler) {
          return;
        }

        if (customErrorHandler) {
          customErrorHandler(event.action.error);
          return;
        }

        handleApiError({ error: event.action.error, showToast: true }, { addToast, formatMessage });
      }),
    [queryClient, addToast, formatMessage],
  );

  return <BaseQueryClientProvider client={queryClient}>{children}</BaseQueryClientProvider>;
};

const skipAuth = typeof SKIP_AUTH !== 'undefined' && SKIP_AUTH === 'true' && PROD !== 'true';

export const ProvidersTree = ({ children }: PropsWithChildren) => {
  const inner = (
    <JotaiProvider>
      <IntlProvider>
        <QueryClientProvider>{children}</QueryClientProvider>
      </IntlProvider>
    </JotaiProvider>
  );

  if (skipAuth) {
    return <DevAuth0Provider>{inner}</DevAuth0Provider>;
  }

  return <Auth0Provider {...auth0ProviderOptions}>{inner}</Auth0Provider>;
};
